import React, { useEffect } from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachingConstants from 'coaching/CoachingConstants';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import List from 'components/List';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import EventTracking from 'lib/EventTracking';
import {
  userReadableDateTimeFormat,
  extractExceptionDatesToUserReadableStrings,
} from 'lib/dateTimeFormat';
import { CoachingEventDefinitionInput } from 'globalTypes';

export type Props = {
  streamUuid: string;
  eventDefinition?: CoachingEventDefinitionInput;
  eventPrefix?: string;
};

export default function BookSessionConfirmationSidebar({
  streamUuid,
  eventDefinition,
  eventPrefix,
}: Props) {
  function trackKey(key: string) {
    eventPrefix && EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    trackKey('view');
  }, []);

  const unavailableDates = extractExceptionDatesToUserReadableStrings(
    eventDefinition?.recurrence ?? []
  );
  return (
    <>
      <BoxBody size="xsmall">
        <BookingConfirmationHeader startDateTime={eventDefinition?.startDateTime} />
        <Divider />
        {unavailableDates.length > 0 ? (
          <BookingUnavailableDatesSection
            data-test="unavailable-dates-section"
            unavailableDates={unavailableDates}
          />
        ) : (
          <BookingNextStepsSection
            data-test="next-steps-section"
            streamUuid={streamUuid}
            eventKey={`${eventPrefix}.message_coach.click`}
          />
        )}
      </BoxBody>
      <Divider size="xxsmall" />
      <BoxBody size="xxsmall">
        <Link weight="semibold" href={LinkConstants.COACHING_HELP_SCHEDULING} target="_blank">
          Review our scheduling policy
        </Link>
      </BoxBody>
    </>
  );
}

function BookingConfirmationHeader({ startDateTime }: { startDateTime?: string }) {
  const formattedDate = startDateTime && userReadableDateTimeFormat(startDateTime);
  return (
    <>
      <Heading level="2">You're all set!</Heading>
      Your session is scheduled{' '}
      {formattedDate && (
        <>
          for <Text weight="semibold">{formattedDate}</Text>
        </>
      )}
      . A confirmation has been emailed to you for your records.
    </>
  );
}

function BookingNextStepsSection({
  streamUuid,
  eventKey,
}: {
  streamUuid: string;
  eventKey?: string;
}) {
  return (
    <>
      <Heading level="4">
        <Text weight="normal">Next steps</Text>
      </Heading>
      <Row size="small">
        Before your session, say hello and share the homework you'd like to go over with your coach.
      </Row>
      <Button
        eventKey={eventKey}
        level="secondary"
        color="graphite"
        full
        to={`${CoachingConstants.COACHING_CHAT_STREAM_ROUTE_PREFIX}/${streamUuid}`}>
        Message coach
      </Button>
    </>
  );
}

function BookingUnavailableDatesSection({ unavailableDates }: { unavailableDates: string[] }) {
  return (
    <>
      <Heading level="4">
        <TextWithIcon weight="normal" icon="info-circle" center={false}>
          Note
        </TextWithIcon>
      </Heading>
      <Row size="small">
        <Box noBorder noShadow textAlign="left" color="paper">
          <BoxBody size="xsmall">
            <Row size="xsmall">Your coach is unavailable for the following sessions:</Row>
            <List delimiter="bullet" markerPosition="inside">
              {unavailableDates.map(d => (
                <li key={d}>{d}</li>
              ))}
            </List>
          </BoxBody>
        </Box>
      </Row>
      You may reschedule or work with another coach for these sessions.
    </>
  );
}
